import { useState, useEffect } from 'react'
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, TextField, Grid, Typography } from '@mui/material'
import { permissionsRoutes, rolesRoutes } from '../routes'
import { useUsersContext, useStatus } from '../context'

const EditRoleDialog = ({ open, onClose, role }) => {
    const { getRoles } = useUsersContext()
    const { setstatus } = useStatus()
    const [roleData, setRoleData] = useState({ name: '', dbName: '', description: '' })
    const [permissions, setPermissions] = useState([])
    const [selectedPermissions, setSelectedPermissions] = useState([])

    useEffect(() => {
        if (open) {
            // Pre-populate the form with the role being edited
            if (role) {
                setRoleData({
                    name: role.name,
                    dbName: role.dbName,
                    description: role.description
                })
                setSelectedPermissions(role.permissions.map(p => typeof p === 'object' ? p._id : p))
            }

            const fetchPermissions = async () => {
                try {
                    const response = await permissionsRoutes.getPermissions()
                    setPermissions(response.data)
                } catch (error) {
                    console.error('Failed to fetch permissions:', error)
                    setstatus({
                        status: 'error',
                        title: 'Error al Cargar Permisos',
                        message: 'No se pudieron cargar los permisos del sistema.',
                        severity: 'error'
                    })
                }
            }
            fetchPermissions()
        }
    }, [open, role])

    const handleChange = (event) => {
        const { name, value } = event.target
        setRoleData((prevData) => ({
            ...prevData,
            [name]: value
        }))
    }

    const handlePermissionChange = (event) => {
        const { value, checked } = event.target
        if (checked) {
            setSelectedPermissions((prev) => [...prev, value])
        } else {
            setSelectedPermissions((prev) => prev.filter((id) => id !== value))
        }
    }

    const handleSave = async () => {
        const finalRoleData = {
            name: roleData.name,
            description: roleData.description,
            dbName: roleData.dbName,
            permissions: selectedPermissions
        }
        try {
            await rolesRoutes.updateRole(role._id, finalRoleData)
            try { if (getRoles) await getRoles() } catch (e) {}
            setstatus({
                status: 'completed',
                title: 'Rol Actualizado',
                message: `El rol "${roleData.name}" ha sido actualizado correctamente.`,
                severity: 'success'
            })
            onClose(true)
        } catch (error) {
            console.error('Error updating role:', error)
            setstatus({
                status: 'error',
                title: 'Error al Actualizar Rol',
                message: error.response?.data?.message || 'Ocurrió un error al actualizar el rol.',
                severity: 'error'
            })
        }
    }

    // Group permissions for better visualization
    const groupedPermissions = permissions.reduce((acc, permission) => {
        const group = permission.key.split('_')[0] // Group by first part of the key (e.g., 'CREATE')
        if (!acc[group]) {
            acc[group] = []
        }
        acc[group].push(permission)
        return acc
    }, {})

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Editar Rol: {role?.name}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    name="name"
                    label="Nombre del Rol"
                    type="text"
                    fullWidth
                    variant="standard"
                    value={roleData.name}
                    onChange={handleChange}
                />
                <TextField
                    margin="dense"
                    name="dbName"
                    label="Nombre en BD (No se puede editar)"
                    type="text"
                    fullWidth
                    variant="standard"
                    value={roleData.dbName}
                    disabled // dbName should not be editable
                />
                <TextField
                    margin="dense"
                    name="description"
                    label="Descripción"
                    type="text"
                    fullWidth
                    multiline
                    rows={2}
                    variant="standard"
                    value={roleData.description}
                    onChange={handleChange}
                />
                <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '10px' }}>
                    Permisos
                </Typography>
                <FormGroup>
                    <Grid container spacing={2}>
                        {Object.entries(groupedPermissions).map(([group, perms]) => (
                            <Grid item xs={12} sm={6} md={4} key={group}>
                                <Typography variant="subtitle1" style={{ textTransform: 'capitalize', fontWeight: 'bold' }}>{group}</Typography>
                                {perms.map((permission) => (
                                    <FormControlLabel
                                        key={permission._id}
                                        control={
                                            <Checkbox
                                                checked={selectedPermissions.includes(permission._id)}
                                                onChange={handlePermissionChange}
                                                value={permission._id}
                                            />
                                        }
                                        label={`${permission.key} (${permission.description})`}
                                    />
                                ))}
                            </Grid>
                        ))}
                    </Grid>
                </FormGroup>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={handleSave} variant="contained">Guardar Cambios</Button>
            </DialogActions>
        </Dialog>
    )
}

export default EditRoleDialog
